import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

interface RestrictedProps {
  title?: string;
  message?: string;
  showUpgrade?: boolean;
  showBackButton?: boolean;
}

export default function Restricted({
  title = "Access Restricted",
  message = "You don't have permission to access this page. Please upgrade your subscription or contact your administrator.",
  showUpgrade = true,
  showBackButton = true,
}: RestrictedProps) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(168, 85, 247, 0.03) 100%)",
        py: 8,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
              color: "white",
              p: 4,
              position: "relative",
              overflow: "hidden",
              textAlign: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "3px solid rgba(255,255,255,0.3)",
                mb: 2,
              }}
            >
              <LockIcon sx={{ fontSize: 50 }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                position: "relative",
              }}
            >
              {title}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 4, lineHeight: 1.7 }}
            >
              {message}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
              }}
            >
              {showBackButton && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  Go Back
                </Button>
              )}

              {showUpgrade && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<UpgradeIcon />}
                  onClick={() => navigate("/account")}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                    background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)",
                    },
                  }}
                >
                  Manage Subscription
                </Button>
              )}
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Need help?{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => navigate("/support")}
                >
                  Contact Support
                </Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
